import { FormEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  TechnologyApplication,
  TechnologyApplicationHosting,
  TechnologyInfrastructureAsset,
  createTechnologyApplicationHosting,
  deleteTechnologyApplicationHosting,
  listTechnologyApplicationHostings,
  listTechnologyInfrastructureAssets,
  updateTechnologyApplicationHosting,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

type HostingFormState = {
  environmentKind: string;
  infrastructureAssetId: string;
  address: string;
};

const emptyForm: HostingFormState = {
  environmentKind: '',
  infrastructureAssetId: '',
  address: '',
};

function infrastructureItemLabel(parts: {
  name: string;
  vendor?: string | null;
  ipAddress?: string | null;
}): string {
  return [parts.name, parts.vendor, parts.ipAddress]
    .map((value) => value?.trim() ?? '')
    .filter((value) => value.length > 0)
    .join(' · ');
}

type TechnologyApplicationHostingModalProps = {
  open: boolean;
  application: Pick<TechnologyApplication, 'id' | 'name'>;
  canWrite: boolean;
  onClose: () => void;
  onChanged?: () => void;
};

export function TechnologyApplicationHostingModal({
  open,
  application,
  canWrite,
  onClose,
  onChanged,
}: TechnologyApplicationHostingModalProps) {
  const { confirm } = useConfirmDialog();
  const formRef = useRef<HTMLFormElement>(null);
  const [hostings, setHostings] = useState<TechnologyApplicationHosting[]>([]);
  const [assets, setAssets] = useState<TechnologyInfrastructureAsset[]>([]);
  const [form, setForm] = useState<HostingFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadHostings(): Promise<void> {
    setLoading(true);
    try {
      const hostingResult = await listTechnologyApplicationHostings(application.id);
      setHostings(hostingResult);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar a hospedagem.'));
    } finally {
      setLoading(false);
    }

    try {
      const assetResult = await listTechnologyInfrastructureAssets({ page: 1, pageSize: 200 });
      setAssets(assetResult.items);
    } catch {
      setAssets([]);
    }
  }

  useEffect(() => {
    if (!open) return;
    setEditingId(null);
    setForm(emptyForm);
    void loadHostings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, application.id]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;
    if (!form.environmentKind || !form.infrastructureAssetId) {
      toast.error('Informe o ambiente e a infraestrutura.');
      return;
    }

    const payload = {
      environmentKind: form.environmentKind,
      infrastructureAssetId: form.infrastructureAssetId,
      address: form.address.trim() || null,
    };
    const wasEditing = Boolean(editingId);

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyApplicationHosting(application.id, editingId, payload);
        toast.success('Hospedagem atualizada.');
      } else {
        await createTechnologyApplicationHosting(application.id, payload);
        toast.success('Hospedagem registrada.');
      }
      setEditingId(null);
      setForm(emptyForm);
      await loadHostings();
      onChanged?.();
    } catch (error) {
      toast.error(
        getRequestErrorMessage(
          error,
          wasEditing ? 'Falha ao atualizar hospedagem.' : 'Falha ao registrar hospedagem.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item: TechnologyApplicationHosting): void {
    setEditingId(item.id);
    setForm({
      environmentKind: item.environmentKind,
      infrastructureAssetId: item.infrastructureAssetId,
      address: item.address ?? '',
    });
    formRef.current?.scrollIntoView({ block: 'nearest' });
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleDelete(item: TechnologyApplicationHosting): Promise<void> {
    if (!canWrite) return;
    const environmentLabel = technologyOptionLabel(
      TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
      item.environmentKind,
    );
    const infrastructureLabel = infrastructureItemLabel({
      name: item.infrastructureAssetName,
      vendor: item.infrastructureVendor,
      ipAddress: item.infrastructureIpAddress,
    });
    const accepted = await confirm({
      title: 'Remover hospedagem',
      message: `Remover ${application.name} de ${infrastructureLabel} no ambiente ${environmentLabel}?`,
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyApplicationHosting(application.id, item.id);
      if (editingId === item.id) {
        setEditingId(null);
        setForm(emptyForm);
      }
      toast.success('Hospedagem removida.');
      await loadHostings();
      onChanged?.();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao remover hospedagem.'));
    }
  }

  const editingItem = hostings.find((item) => item.id === editingId) ?? null;

  return (
    <DraggableModalPanel
      open={open}
      title="Hospedagem"
      subtitle={`${application.name} nos ativos de infraestrutura`}
      onClose={onClose}
      className="w-full max-w-3xl"
      footer={
        <FormActionBar className="erp-btn-actions">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </FormActionBar>
      }
    >
      <div className="space-y-4 p-1">
        <p className="text-sm text-secondary-600">
          Informe o ambiente e a infraestrutura de cada implantação. O endereço é opcional. A mesma
          aplicação pode ter várias hospedagens, inclusive mais de uma do mesmo ambiente.
        </p>

        {canWrite ? (
          <form
            ref={formRef}
            onSubmit={(event) => void handleSubmit(event)}
            className={`erp-form ${editingId ? 'erp-form-card--editing rounded-xl p-3' : ''}`}
          >
            <div className="erp-form-grid">
              <label className="erp-label">
                <FieldLabel
                  required
                  description="Estágio em que esta implantação roda."
                  example="Produção"
                >
                  Ambiente
                </FieldLabel>
                <select
                  className="erp-input mt-1"
                  value={form.environmentKind}
                  onChange={(event) => setForm({ ...form, environmentKind: event.target.value })}
                  required
                >
                  <option value="">Selecione</option>
                  {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                <FieldLabel
                  required
                  description="Instância concreta (servidor, banco, storage…) onde a aplicação roda."
                  example="pg-prod-01"
                >
                  Infraestrutura
                </FieldLabel>
                <select
                  className="erp-input mt-1"
                  value={form.infrastructureAssetId}
                  onChange={(event) =>
                    setForm({ ...form, infrastructureAssetId: event.target.value })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {editingItem &&
                  !assets.some((asset) => asset.id === editingItem.infrastructureAssetId) ? (
                    <option value={editingItem.infrastructureAssetId}>
                      {infrastructureItemLabel({
                        name: editingItem.infrastructureAssetName,
                        vendor: editingItem.infrastructureVendor,
                        ipAddress: editingItem.infrastructureIpAddress,
                      })}
                    </option>
                  ) : null}
                  {assets.map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {infrastructureItemLabel(asset)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label sm:col-span-2">
                <FieldLabel
                  description="Endereço em que esta implantação é acessada, quando houver."
                  example="https://app.exemplo.com"
                >
                  Endereço
                </FieldLabel>
                <input
                  className="erp-input mt-1"
                  value={form.address}
                  onChange={(event) => setForm({ ...form, address: event.target.value })}
                  placeholder="https://"
                />
              </label>
            </div>
            {assets.length === 0 && !editingId ? (
              <p className="mt-2 text-sm text-secondary-500">
                Cadastre um ativo de infraestrutura antes de registrar hospedagem.
              </p>
            ) : null}
            <FormActionBar
              className="erp-btn-actions mt-4"
              showCancel={!!editingId}
              onCancel={cancelEdit}
            >
              <button
                type="submit"
                disabled={saving || (!editingId && assets.length === 0)}
                className="erp-btn-primary"
              >
                {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Adicionar hospedagem'}
              </button>
            </FormActionBar>
          </form>
        ) : null}

        {loading ? (
          <p className="text-secondary-500">Carregando...</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-secondary-200">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Ambiente</th>
                  <th>Infraestrutura</th>
                  <th>Endereço</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {hostings.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 4 : 3} className="text-center text-secondary-500">
                      Nenhuma hospedagem registrada.
                    </td>
                  </tr>
                ) : (
                  hostings.map((item) => (
                    <tr key={item.id} className={item.id === editingId ? 'bg-primary-50' : undefined}>
                      <td>
                        {technologyOptionLabel(
                          TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
                          item.environmentKind,
                        )}
                      </td>
                      <td>
                        {infrastructureItemLabel({
                          name: item.infrastructureAssetName,
                          vendor: item.infrastructureVendor,
                          ipAddress: item.infrastructureIpAddress,
                        })}
                      </td>
                      <td>{item.address?.trim() || '—'}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Remover"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DraggableModalPanel>
  );
}
